import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { ThemeProvider } from './context/ThemeContext';
import { useVendor } from './context/VendorContext';
import AnalyticsProvider from './components/AnalyticsProvider';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Home from './pages/Home';
import { trackLogin, trackLogout } from './utils/analytics';
import FeedbackWidget from './components/FeedbackWidget';
import * as api from './services/api';
import './styles/themes.css';
import './App.css';

// Lazy load non-landing pages for code splitting
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const VaultPage = React.lazy(() => import('./pages/VaultPage'));
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4003/api';

// Redirect to external URL
function ExternalRedirect({ url }) {
  useEffect(() => { window.location.href = url; }, [url]);
  return null;
}

function App() {
  const { ready, authenticated, user: privyUser, login, logout: privyLogout, getAccessToken } = usePrivy();
  const { features, links } = useVendor();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const syncWithBackend = useCallback(async () => {
    if (!authenticated || !privyUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_URL}/auth/privy-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success && data.data?.user) {
        setUser(data.data.user);
        trackLogin('privy');
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Backend sync error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authenticated, privyUser, getAccessToken]);

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.data?.user) {
        setUser(data.data.user);
      }
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (authenticated && privyUser) {
      syncWithBackend();
    } else {
      checkSession();
    }
  }, [ready, authenticated, privyUser, syncWithBackend, checkSession]);

  // Fetch conversations when user is set
  const refreshConversations = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.getConversations();
      setConversations(data.data?.conversations || []);
    } catch {
      // Silently fail — user may not be authenticated yet
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshConversations();
    } else {
      setConversations([]);
    }
  }, [user, refreshConversations]);

  // Patch name from passport data if not set in user DB record
  useEffect(() => {
    if (!user) return;
    if (user.first_name) return; // already has a name from DB
    api.getVaultProfile().then(res => {
      // Passport name is in passportInfo.fullName — split into first/last
      const passportName = res?.data?.passportInfo?.fullName;
      if (passportName) {
        const parts = passportName.trim().split(/\s+/);
        const first = parts[0] || '';
        const last = parts.slice(1).join(' ') || '';
        setUser(prev => ({ ...prev, first_name: first, last_name: last }));
      }
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try {
      trackLogout();
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      await privyLogout();
      setUser(null);
      setConversations([]);
      // Clear anonymous chat session so a fresh chat starts after logout
      sessionStorage.removeItem('anonChatMessages');
      sessionStorage.removeItem('anonChatAppCtx');
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      sessionStorage.removeItem('anonChatMessages');
      sessionStorage.removeItem('anonChatAppCtx');
    }
  };

  const handleShowAuthModal = () => {
    login();
  };

  if (!ready) {
    return (
      <div className="v2-loading-screen">
        <div className="v2-loading-spinner" />
      </div>
    );
  }

  const chatPageProps = {
    user,
    authLoading: loading,
    onShowAuthModal: handleShowAuthModal,
    onConversationChange: setActiveConversationId,
    refreshConversations,
  };

  return (
    <ThemeProvider user={user}>
      <BrowserRouter>
        <AnalyticsProvider user={user}>
          <div className={`v2-app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <Sidebar
              user={user}
              onShowAuthModal={handleShowAuthModal}
              collapsed={sidebarCollapsed}
              onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
              conversations={conversations}
              activeConversationId={activeConversationId}
              onFeedbackOpen={() => setFeedbackOpen(true)}
            />
            <div className="v2-main">
              <TopBar
                user={user}
                loading={loading}
                onLogout={handleLogout}
                onShowAuthModal={handleShowAuthModal}
              />
              <main className="v2-page-content">
                <Suspense fallback={<div className="v2-loading-screen"><div className="v2-loading-spinner" /></div>}>
                  <Routes>
                    <Route path="/" element={
                      <Home user={user} onShowAuthModal={handleShowAuthModal} />
                    } />

                    {features.chat && (
                      <>
                        <Route path="/chat" element={<ChatPage {...chatPageProps} />} />
                        <Route path="/chat/:conversationId" element={<ChatPage {...chatPageProps} />} />
                      </>
                    )}

                    {features.history && (
                      <Route path="/history" element={
                        <HistoryPage
                          user={user}
                          onShowAuthModal={handleShowAuthModal}
                          conversations={conversations}
                          refreshConversations={refreshConversations}
                        />
                      } />
                    )}

                    {features.explore && (
                      <Route path="/explore" element={
                        <ExplorePage user={user} onShowAuthModal={handleShowAuthModal} />
                      } />
                    )}

                    {features.vault && (
                      <Route path="/vault" element={
                        <VaultPage user={user} onShowAuthModal={handleShowAuthModal} />
                      } />
                    )}

                    <Route path="/terms" element={
                      links.termsUrl.startsWith('http')
                        ? <ExternalRedirect url={links.termsUrl} />
                        : <TermsPage />
                    } />
                    <Route path="/privacy" element={
                      links.privacyUrl.startsWith('http')
                        ? <ExternalRedirect url={links.privacyUrl} />
                        : <PrivacyPage />
                    } />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </main>
            </div>
          </div>
          {features.feedback && (
            <FeedbackWidget isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
          )}
        </AnalyticsProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
